<template>
  <section id="hero-carousel">
    <v-carousel
      v-if="!loading"
      :cycle="cycle"
      :interval="interval"
      :show-arrows="showArrows"
      :hide-delimiters="hideDelimiters"
      class="hero-carousel"
    >
      <v-carousel-item
        v-for="slide in slides"
        :key="slide.id"
      >
        <div
          class="carousel-slide"
          :class="{ 'clickable': slide.clickable }"
          @click="handleSlideClick(slide)"
        >
          <v-img
            v-if="slide.imageSrc"
            :src="slide.imageSrc"
            position="center center"
            contain
            class="white--text d-flex justify-center align-center carousel-image"
          />
          <div
            v-if="slide.title"
            class="slide-title"
            :style="{
              top: slide.titleTop || '60%',
              left: slide.titleLeft || '10%',
            }"
          >
            {{ $t(slide.title) }}
          </div>
          <div
            v-if="slide.subtitle"
            class="slide-subtitle"
            :style="{
              top: slide.subtitleTop || '76%',
              left: slide.subtitleLeft || '10%',
            }"
          >
            {{ $t(slide.subtitle) }}
          </div>
        </div>
      </v-carousel-item>
    </v-carousel>
  </section>
</template>

<script>
  import carouselData from '@/data/carousel.json'

  export default {
    name: 'HeroCarousel',

    provide: {
      theme: { isDark: true },
    },

    props: {
      cycle: {
        type: Boolean,
        default: true,
      },
      interval: {
        type: Number,
        default: 5000,
      },
      showArrows: {
        type: Boolean,
        default: true,
      },
      hideDelimiters: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        slides: [],
        loading: true,
      }
    },

    mounted() {
      this.loadSlides()
    },

    methods: {
      // 获取图片路径，使用静态 require 确保 Webpack 可以处理
      getImageSrc(imagePath) {
        // 创建图片路径映射表，使用静态 require
        const imageMap = {
          'product/2/img/banner.jpg': require('@/assets/product/2/img/banner.jpg'),
          'product/7/img/banner.webp': require('@/assets/product/7/img/banner.webp'),
          // 可以继续添加其他图片路径
        }
        // 如果路径在映射表中，返回对应的 require
        if (imageMap[imagePath]) {
          return imageMap[imagePath]
        }
        // 如果不在映射表中，尝试动态 require（可能失败）
        try {
          // 尝试直接 require，但 Webpack 可能无法处理
          return require(`@/assets/${imagePath}`)
        } catch (error) {
          console.warn(`Image not found in map and require failed: ${imagePath}`, error)
          return ''
        }
      },
      loadSlides() {
        this.loading = true
        try {
          const loadedSlides = carouselData.slides.map(slide => {
            const imageSrc = this.getImageSrc(slide.url)
            return {
              ...slide,
              imageSrc: imageSrc || '',
            }
          })
          this.slides = loadedSlides
          console.log('Loaded slides:', this.slides)
        } catch (error) {
          console.error('Failed to load slides:', error)
          this.slides = carouselData.slides.map(slide => ({ ...slide, imageSrc: '' }))
        } finally {
          this.loading = false
        }
      },
      handleSlideClick(slide) {
        if (slide.clickable && slide.route) {
          this.$router.push(slide.route)
        }
      },
    },
  }
</script>

<style lang="sass" scoped>
// Hero轮播区域样式
#hero-carousel
  position: relative

.hero-carousel
  // 让轮播容器高度自适应内容
  height: auto !important
  margin: 0 !important
  padding: 0 !important

  // 强制覆盖 Vuetify 默认的固定高度
  ::v-deep .v-window
    height: auto !important

  ::v-deep .v-window__container
    height: auto !important

  ::v-deep .v-carousel__wrapper
    height: auto !important
    margin: 0 !important
    padding: 0 !important

  ::v-deep .v-window-item
    height: auto !important

  ::v-deep .v-carousel__item
    height: auto !important
    margin: 0 !important
    padding: 0 !important

  // 完全隐藏控制按钮和指示器
  ::v-deep .v-carousel__controls
    display: none !important

  ::v-deep .v-carousel__prev,
  ::v-deep .v-carousel__next
    display: none !important

  ::v-deep .v-carousel__delimiters
    display: none !important

// 轮播幻灯片容器
.carousel-slide
  position: relative
  width: 100%
  height: auto
  margin: 0 !important
  padding: 0 !important
  display: block

  &.clickable
    cursor: pointer
    transition: opacity 0.3s ease

    &:hover
      opacity: 0.9

// 确保图片完全展示，容器高度自适应
.carousel-image
  position: relative
  width: 100%
  margin: 0 !important
  padding: 0 !important
  display: block !important
  vertical-align: top !important
  // 移除所有固定高度和比例限制，让图片按原始比例显示
  // 不设置 aspect-ratio，让图片自然高度决定容器高度

  // v-img 相关样式，让它正常工作
  ::v-deep .v-responsive
    margin: 0 !important
    padding: 0 !important
    display: block !important
    height: auto !important
    position: relative !important

  // 保留 v-responsive__sizer，它用于保持图片宽高比
  ::v-deep .v-responsive__sizer
    display: block !important

  ::v-deep .v-responsive__content
    margin: 0 !important
    padding: 0 !important
    display: block !important
    height: auto !important
    position: relative !important
    width: 100% !important

  ::v-deep .v-image__image
    margin: 0 !important
    padding: 0 !important
    display: block !important
    vertical-align: top !important
    // 使用 contain 模式，确保图片完整显示
    background-size: contain !important
    background-repeat: no-repeat !important
    background-position: center center !important
    width: 100% !important

// 标题样式
.slide-title
  text-align: left
  font-size: 5vw
  color: #333333
  font-weight: 500
  line-height: 120%
  position: absolute
  // 位置通过动态 style 设置
  // 确保不超出容器
  word-wrap: break-word
  overflow-wrap: break-word
  white-space: normal
  z-index: 10
  pointer-events: none
  // 限制在容器内的最大宽度
  max-width: 80vw

// 副标题样式
.slide-subtitle
  text-align: left
  font-size: 3vw
  color: #9f9f9f
  font-weight: 400
  text-transform: capitalize
  line-height: 1.2
  position: absolute
  // 位置通过动态 style 设置
  // 确保不超出容器
  word-wrap: break-word
  overflow-wrap: break-word
  white-space: normal
  z-index: 10
  pointer-events: none
  // 限制在容器内的最大宽度
  max-width: 80vw

// 响应式设计
@media (max-width: 768px)
  .slide-title
    font-size: 6vw
    // 位置通过动态 style 设置，响应式时可以使用默认值或覆盖

  .slide-subtitle
    font-size: 3.6vw
    // 位置通过动态 style 设置，响应式时可以使用默认值或覆盖
</style>
